import { Badge } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Palette } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { BrandSummary } from "@/api";
import { listBrandsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { EntityListPage } from "@/components/build/EntityListPage";
import { EntityRow } from "@/components/build/EntityRow";
import { byName, byNewest, type ListSort, useListControls } from "@/components/build/useListControls";
import { BuildWithAiButton } from "@/features/build/withAi/BuildWithAiButton";
import { BrandIcon } from "./BrandIcon";
import { useDeleteBrand } from "./useDeleteBrand";

/** "Mine" = any membership role (same as agents and data sources). The company default is everyone's, not mine. */
const isMine = (b: BrandSummary) => Boolean(b.currentUserRole);

/** The company default brand leads the list, whatever the sort. */
const defaultFirst =
  (compare: ListSort<BrandSummary>["compare"]): ListSort<BrandSummary>["compare"] =>
  (a, b) =>
    Number(b.isDefault ?? false) - Number(a.isDefault ?? false) || compare(a, b);

/** Matches the name or the audience. */
const matches = (b: BrandSummary, term: string) =>
  b.name.toLowerCase().includes(term) || (b.audience ?? "").toLowerCase().includes(term);

/** Build > Brands list: search, All/Mine and sort like the other Build lists. Default brand first. */
export function BrandsListPage() {
  const { t } = useTranslation(["build", "brands"]);
  const navigate = useNavigate();
  const { onDelete, dialog } = useDeleteBrand();
  const { search, apply, listProps } = useListControls<BrandSummary>({
    isMine,
    sorts: [
      { value: "name", label: t("build:list.sort.name"), compare: defaultFirst(byName) },
      { value: "newest", label: t("build:list.sort.newest"), compare: defaultFirst(byNewest) },
    ],
  });

  const query = useQuery(listBrandsOptions({ query: { page_size: 50 } }));
  const term = search.trim().toLowerCase();
  // Searched here (the shared search matches the name only), then filtered and sorted by the shared controls.
  const brands = apply(
    (query.data?.data ?? []).filter((b) => !term || matches(b, term)),
    { searched: true },
  );

  return (
    <EntityListPage
      title={t("brands:title")}
      subtitle={t("brands:subtitle")}
      newLabel={t("brands:new")}
      onNew={() => navigate("/build/brands/new")}
      headerActions={<BuildWithAiButton entity="brand" />}
      {...listProps}
    >
      {brands.map((brand) => (
        <EntityRow
          key={brand.uuid}
          icon={<BrandIcon />}
          name={brand.name}
          description={brand.description}
          meta={
            <>
              {brand.isDefault && <Badge variant="brand">{t("brands:default")}</Badge>}
              {brand.audience && (
                <Badge variant="neutral" size="compact">
                  {brand.audience === "internal" || brand.audience === "external"
                    ? t(`brands:audience.${brand.audience}`)
                    : brand.audience}
                </Badge>
              )}
            </>
          }
          onOpen={() => navigate(`/build/brands/${brand.uuid}`)}
          menuItems={[
            { label: t("build:menu.edit"), onSelect: () => navigate(`/build/brands/${brand.uuid}`) },
            ...(brand.isDefault
              ? []
              : [{ label: t("build:menu.delete"), danger: true, onSelect: () => onDelete(brand) }]),
          ]}
        />
      ))}
      {brands.length === 0 &&
        (search ? (
          <EmptyState title={t("build:list.noResultsTitle")} body={t("build:list.noResultsBody", { query: search })} />
        ) : (
          <EmptyState icon={<Palette size={32} />} title={t("brands:empty.title")} body={t("brands:empty.body")} />
        ))}
      {dialog}
    </EntityListPage>
  );
}
